import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Plus, Route as RouteIcon, Building2, UserRound, AlertTriangle, RotateCw, Loader2 } from 'lucide-react';
import ProgrammeTemplateCard from '../programmes/ProgrammeTemplateCard';
import ProgrammeEditor from '../programmes/ProgrammeEditor';
import useProgrammeActions from '../programmes/useProgrammeActions';
import { fetchProgrammes } from '../../redux/programmeSlice';
import { groupByMeantFor } from '../../utils/careProgrammes';
import { serverReason } from '../patientTabs/prescriptionHelpers';

// SETTINGS → PROGRAMMES (doctor), beside Check sets (care programmes, Round
// B). The doctor's own programmes (create / edit / delete) and the hospital's
// (read + "Copy to my programmes"). Every programme is the doctor's own
// definition — what the start date is called, how long it runs, each
// milestone and the check set that proves it; nothing is pre-made or
// speciality-specific (owner, 26 Sep 2026).
//
// The HOSPITAL ADMIN gets the same screen for any doctor of the hospital
// (HospitalProgrammes passes `doctor` — { doctorId, name }): GET
// ?purpose=manage&doctorId=, a doctor programme created with its doctorId,
// "Copy to this doctor's programmes" with the doctorId, and the milestone set
// picker offering THAT doctor's sets. The server enforces who may.

function ProgrammesTab({ doctor = null }) {
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();
    const forAdmin = Boolean(doctor?.doctorId);
    const doctorId = forAdmin ? doctor.doctorId : null;
    const doctorName = forAdmin ? (doctor.name || t('common.doctor_label', 'Doctor')) : '';
    const { manage, busyId } = useSelector((state) => state.programmes);
    const { remove, copy } = useProgrammeActions();
    const [editor, setEditor] = useState({ open: false, programme: null });

    const load = () => dispatch(fetchProgrammes(forAdmin ? { doctorId } : {}));

    useEffect(() => {
        setEditor({ open: false, programme: null });
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, doctorId]);

    // The list on screen must be THIS doctor's (the store holds one at a time).
    const current = manage.doctorId === doctorId;
    const loaded = current && manage.loaded;
    const loadError = current ? manage.error : null;
    const hospitalGroups = groupByMeantFor(manage.hospital);

    return (
        <div data-testid={forAdmin ? 'doctor-programmes' : 'programmes-tab'} className="space-y-6">
            {!forAdmin && (
                <div>
                    <h3 className="flex items-center gap-2 text-base font-semibold text-gray-800">
                        <RouteIcon className="h-5 w-5 text-[#4ABDBD]" aria-hidden="true" />
                        {t('programmes.heading')}
                    </h3>
                    <p className="mt-1 max-w-3xl text-sm text-gray-500">{t('programmes.subheading')}</p>
                </div>
            )}

            {manage.loading && !loaded && (
                <div data-testid="programmes-loading" className="flex items-center justify-center gap-2 py-8 text-sm text-[#4ABDBD]">
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                    {t('programmes.loading')}
                </div>
            )}

            {loadError && (
                <div role="alert" data-testid="programmes-load-error" className="flex flex-col items-center gap-2 rounded-xl border border-red-100 bg-red-50/60 px-4 py-6 text-center">
                    <p className="flex items-center gap-1.5 text-sm font-semibold text-red-700">
                        <AlertTriangle className="h-4 w-4" aria-hidden="true" />
                        {serverReason(loadError, t('programmes.load_failed'), i18n.language)}
                    </p>
                    <button
                        type="button"
                        data-testid="programmes-retry"
                        onClick={load}
                        className="inline-flex items-center gap-1.5 rounded-xl border border-red-200 bg-white px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-50"
                    >
                        <RotateCw className="h-3.5 w-3.5" aria-hidden="true" />
                        {t('programmes.retry')}
                    </button>
                </div>
            )}

            {loaded && !loadError && (
                <>
                    <section data-testid="programmes-mine">
                        <div className="mb-3 flex flex-wrap items-end justify-between gap-2">
                            <div className="min-w-0">
                                <h4 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-gray-400">
                                    <UserRound className="h-3.5 w-3.5" aria-hidden="true" />
                                    {forAdmin ? t('programmes.doctor_programmes', { name: doctorName }) : t('programmes.my_programmes')}
                                </h4>
                                <p className="text-xs text-gray-500">{forAdmin ? t('programmes.doctor_programmes_hint') : t('programmes.my_programmes_hint')}</p>
                            </div>
                            <button
                                type="button"
                                data-testid="programmes-new"
                                onClick={() => setEditor({ open: true, programme: null })}
                                className="inline-flex items-center gap-1.5 rounded-xl bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] px-4 py-2 text-sm font-semibold text-white shadow transition hover:shadow-md"
                            >
                                <Plus className="h-4 w-4" aria-hidden="true" />
                                {t('programmes.new_programme')}
                            </button>
                        </div>
                        {manage.mine.length === 0 ? (
                            <p data-testid="programmes-mine-empty" className="rounded-xl bg-gray-50 px-4 py-6 text-center text-sm text-gray-400">
                                {forAdmin ? t('programmes.no_doctor_programmes') : t('programmes.no_my_programmes')}
                            </p>
                        ) : (
                            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
                                {manage.mine.map((p) => (
                                    <ProgrammeTemplateCard
                                        key={p.programmeId}
                                        programme={p}
                                        busy={busyId === p.programmeId}
                                        onEdit={(x) => setEditor({ open: true, programme: x })}
                                        onDelete={remove}
                                    />
                                ))}
                            </div>
                        )}
                    </section>

                    <section data-testid="programmes-hospital">
                        <h4 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-gray-400">
                            <Building2 className="h-3.5 w-3.5" aria-hidden="true" />
                            {t('programmes.hospital_programmes')}
                        </h4>
                        <p className="mb-3 text-xs text-gray-500">{forAdmin ? t('programmes.hospital_programmes_hint_admin') : t('programmes.hospital_programmes_hint')}</p>
                        {manage.hospital.length === 0 ? (
                            <p data-testid="programmes-hospital-empty" className="rounded-xl bg-gray-50 py-6 text-center text-sm text-gray-400">{t('programmes.no_hospital_programmes')}</p>
                        ) : (
                            <div className="space-y-4">
                                {hospitalGroups.map((g) => (
                                    <div key={g.label || '_general'}>
                                        <p className="mb-2 text-xs font-semibold text-[#2D8A8A]">{g.label || t('programmes.general_group')}</p>
                                        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
                                            {g.programmes.map((p) => (
                                                <ProgrammeTemplateCard
                                                    key={p.programmeId}
                                                    programme={p}
                                                    busy={busyId === p.programmeId}
                                                    onCopy={(x) => (forAdmin ? copy(x, { doctorId }) : copy(x))}
                                                    copyLabel={forAdmin ? t('programmes.copy_to_doctor') : null}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </section>
                </>
            )}

            <ProgrammeEditor
                isOpen={editor.open}
                ownerType="doctor"
                doctorId={doctorId}
                programme={editor.programme}
                onClose={() => setEditor({ open: false, programme: null })}
            />
        </div>
    );
}

export default ProgrammesTab;
